'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { useI18n } from 'next-localization';

import { isFunction } from 'lodash-es';

import { GenericModal } from '@/components/modals';
import { cn, isHospitalSiteType } from '@/lib/utils';
import { useOasApp } from '@/providers/oas-app-provider';
import { NeuSelect } from '@hcahealthcaredpx/neutron-react';

import styles from '../modal.module.css';
import {
  DISCLOSURE_STORAGE_PERIOD,
  ENGLISH_SELECT,
  SAFE_HARBOR_DISCLAIMER_STORAGE_KEY,
} from './constants';
import { DISCLAIMER_MODAL_NAME } from './types';

interface DisclaimerModalProps {
  onSuccess?: (data?: unknown) => void;
  onDecline?: () => void;
  titleTranslationKey?: string;
  modalName: DISCLAIMER_MODAL_NAME;
  acceptButtonTranslationKey?: string;
  declineButtonTranslationKey?: string;
  languageDropdownTranslationKey?: string;
}

// NOTE: Originally built for Safe Harbor disclaimer, but can be used for other disclaimers as well
// Be mindful of the storage key and modal name when reusing
// For example, for Healow disclaimer we shouldn't hide it on PSG sites
export const DisclaimerModal = ({
  onSuccess,
  onDecline,
  titleTranslationKey = 'OAS.LandingPage.DisclaimerModal.Title',
  acceptButtonTranslationKey = 'OAS.LandingPage.DisclaimerModal.Confirmation',
  declineButtonTranslationKey = 'OAS.PageContent.DisclaimerModal.Cancel',
  languageDropdownTranslationKey = 'OAS.LandingPage.DisclaimerModal.Language',
  modalName,
}: DisclaimerModalProps): JSX.Element | null => {
  const fields = useOasApp();
  // Note: do not show modal for physicians (for these users it is not required)
  const isHospital = isHospitalSiteType(fields.siteType);
  const { t } = useI18n();
  const [selectedLanguage, setSelectedLanguage] = useState(ENGLISH_SELECT.value);
  const { disclosureTextEN, disclosureTextES } = useMemo(() => {
    switch (modalName) {
      case DISCLAIMER_MODAL_NAME.SAFE_HARBOR:
        return {
          disclosureTextEN: fields.dshsEnglish || '',
          disclosureTextES: fields.dshsSpanish || '',
        };
      case DISCLAIMER_MODAL_NAME.HEALOW_BOOKING:
        return {
          disclosureTextEN: fields.bookingDsEnglish || '',
          disclosureTextES: fields.bookingDsSpanish || '',
        };
      default:
        return {
          disclosureTextEN: '',
          disclosureTextES: '',
        };
    }
  }, [fields, modalName]);

  const handleModalAccept = useCallback(() => {
    try {
      if (modalName === DISCLAIMER_MODAL_NAME.SAFE_HARBOR) {
        const currentTime = Date.now();
        const disclosureEndingTime = currentTime + DISCLOSURE_STORAGE_PERIOD;
        localStorage.setItem(SAFE_HARBOR_DISCLAIMER_STORAGE_KEY, disclosureEndingTime.toString());
      }

      if (isFunction(onSuccess)) {
        onSuccess();
      }
    } catch (error) {
      console.log(error);
    }
  }, [onSuccess]);

  const handleModalDecline = () => {
    if (isFunction(onDecline)) {
      onDecline();
    }
  };

  // only for Safe Harbor we don't show disclaimer on non-Hospital and Physician sites
  if (modalName === DISCLAIMER_MODAL_NAME.SAFE_HARBOR && !isHospital) return null;

  const formatting = /\\r\\n/g;
  const noTextMessage = t('OAS.LandingPage.DisclaimerModal.NoTextMessage');

  const formattedDisclosureTextEN = disclosureTextEN?.replace(formatting, '\n') ?? noTextMessage;
  const formattedDisclosureTextES = disclosureTextES?.replace(formatting, '\n') ?? noTextMessage;

  // Retains the original API text formatting - new lines, carriage returns
  // Todo: check if we need to use RichText from Sitecore.
  const disclosureText =
    selectedLanguage.toLocaleLowerCase() === ENGLISH_SELECT.value.toLocaleLowerCase()
      ? formattedDisclosureTextEN
      : formattedDisclosureTextES;

  const formatted = () =>
    disclosureText.split('\n').map((line: string, id: number) => (
      <React.Fragment key={`line-${id}`}>
        {line}
        <br />
      </React.Fragment>
    ));

  const selectItems = [
    {
      label: 'English',
      value: 'en-US',
      selected: true,
    },
    {
      label: 'Spanish',
      value: 'es-US',
    },
  ];

  if (!disclosureTextEN?.length) {
    console.error('No dshsEnglish text provided. Cannot show Disclosure Statement.');
  }

  return (
    <>
      {disclosureTextEN && disclosureTextEN?.length > 0 && (
        <GenericModal
          name={modalName}
          label={t(titleTranslationKey, { lng: selectedLanguage })}
          handleModalAccept={handleModalAccept}
          handleModalDecline={handleModalDecline}
          acceptButtonText={t(acceptButtonTranslationKey, {
            lng: selectedLanguage,
          })}
          // Todo: add translation to dictionary
          declineButtonText={t(declineButtonTranslationKey, {
            lng: selectedLanguage,
          })}
        >
          {formattedDisclosureTextEN?.length > 0 &&
            disclosureTextES &&
            disclosureTextES?.length > 0 && (
              <div className={cn('ms-auto', styles.languageSelect)}>
                <NeuSelect
                  label={t(languageDropdownTranslationKey)}
                  items={selectItems}
                  value={selectItems?.find((item) => item.value === selectedLanguage)}
                  onNeuChange={(ev) => setSelectedLanguage(ev.value)}
                  required
                />
              </div>
            )}
          <p className="py-2 mb-0" tabIndex={0}>
            {formatted()}
          </p>
        </GenericModal>
      )}
    </>
  );
};
